import { MenuIcon, PenIcon, TrashIcon } from "@xola/icons";
import { flash, Label, Table } from "@xola/ui-kit";
import clsx from "clsx";
import { DemographicIcon } from "common";
import { produce } from "immer";
import { findIndex, isNil, omit, sortBy, uniqueId } from "lodash";
import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import {
    CatalogItem,
    CatalogItemSequences,
    CatalogItemSequenceType,
    Experience,
    ProductCatalog,
    ProductCatalogItem,
    UnitType,
} from "types";
import {
    getResultingSequence,
    isNewDemographicCatalogItem,
    populateMissingSequences,
    roundDownSequence,
} from "../../../models/CatalogItem";
import { isDefaultDemographic } from "../../../models/DemographicUnit";
import { reorder } from "../../../utils/reorder";
import { InlineConfirmDeletePopover } from "../../Common/InlineConfirmDeletePopover";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { AddCatalogItemPopover } from "./AddCatalogItemPopover";
import { EditCatalogItemModal } from "./EditCatalogItemModal";

interface DemographicCatalogEditorProps {
    value: ProductCatalog;
    experience: Experience;
    error?: string;
    onChange: (catalog: ProductCatalog) => void;
}

export const DemographicCatalogEditor = ({ value, experience, error, onChange }: DemographicCatalogEditorProps) => {
    const catalogItems = value.items.filter((catalogItem) => {
        return catalogItem.unitType === UnitType.DEMOGRAPHIC;
    });
    const isSequenceMissing = catalogItems.find((catalogItem) => !catalogItem.sequence);
    const demographicCatalogItems = (
        isSequenceMissing ? populateMissingSequences(catalogItems) : catalogItems
    ) as ProductCatalogItem[];

    const handleInsertCatalogItem = (catalogItem: ProductCatalogItem) => {
        const existingCatalogItem = demographicCatalogItems.find((item) => {
            return item.template?.id === catalogItem.template?.id;
        });
        if (existingCatalogItem) {
            flash.danger("This demographic already exists");
            return;
        }

        /**
         * If a catalog Item is deleted and re-added again before saving, we will not create a new item
         * instead we will add back the original catalog item so that id will persist.
         */
        const deletedCatalogItem = experience.catalog.items.find((item) => {
            return item.template?.id === catalogItem.template?.id;
        });

        const catalog = produce(value, (draft) => {
            draft.items.push(
                deletedCatalogItem ?? {
                    ...omit(catalogItem, ["id"]),
                    parent: { id: catalogItem.id! },
                },
            );
        });

        const newItems = catalog.items.filter((item) => isNewDemographicCatalogItem(item));

        if (newItems.length > 1) {
            const newCatalog = produce(catalog, (draft) => {
                const items: CatalogItem[] = [];
                const demographicItemsWithSequence = sortBy(
                    catalog.items.filter((item) => item.unitType === UnitType.DEMOGRAPHIC && !isNil(item.sequence)),
                    "sequence",
                );
                let lastUsedSequence =
                    demographicItemsWithSequence?.[demographicItemsWithSequence.length - 1]?.sequence ?? 0;

                for (const item of sortBy(draft.items, "sequence")) {
                    if (isNewDemographicCatalogItem(item) && isNil(item.sequence)) {
                        const newSequence = roundDownSequence(lastUsedSequence) + CatalogItemSequences.STEP;
                        const updatedItem = {
                            ...item,
                            sequence: newSequence,
                        };

                        lastUsedSequence = updatedItem.sequence;
                        items.push(updatedItem);
                    } else {
                        items.push(item);
                    }
                }

                draft.items = items;
            });

            return onChange(newCatalog);
        }

        onChange(catalog);
    };

    const handleDeleteCatalogItem = (catalogItem: ProductCatalogItem) => {
        const catalog = produce(value, (draft) => {
            draft.items = draft.items.filter((item) => {
                return item.template?.id !== catalogItem.template?.id;
            });
        });
        onChange(catalog);
    };

    const handleUpdateCatalogItem = (catalogItem: ProductCatalogItem) => {
        const catalog = produce(value, (draft) => {
            const index = findIndex(draft.items, ["template.id", catalogItem.template?.id]);
            draft.items[index] = catalogItem;
        });

        onChange(catalog);
    };

    return (
        <div className="space-y-2">
            <div className="mb-2 flex items-center justify-between">
                <Label className={error && "!text-danger"}>Demographics {error ? <span>{error}</span> : null}</Label>
                <AddCatalogItemPopover onInsert={handleInsertCatalogItem} />
            </div>
            <DemographicCatalogItemList
                value={value}
                catalogItems={demographicCatalogItems}
                onChange={onChange}
                onDeleteCatalogItem={handleDeleteCatalogItem}
                onUpdateCatalogItem={handleUpdateCatalogItem}
            />
        </div>
    );
};

interface DemographicCatalogItemListProps {
    value: ProductCatalog;
    catalogItems: ProductCatalogItem[];
    onChange: (catalog: ProductCatalog) => void;
    onDeleteCatalogItem: (catalogItem: ProductCatalogItem) => void;
    onUpdateCatalogItem: (catalogItem: ProductCatalogItem) => void;
}

export const DemographicCatalogItemList = ({
    value,
    catalogItems,
    onChange,
    onDeleteCatalogItem,
    onUpdateCatalogItem,
}: DemographicCatalogItemListProps) => {
    const [showEditCatalogItemModal, setShowEditCatalogItemModal] = useState(false);
    const [selectedCatalogItem, setSelectedCatalogItem] = useState<ProductCatalogItem>();
    const [demographicItems, setDemographicItems] = useState<ProductCatalogItem[]>(catalogItems);

    const toggleEditCatalogItemModal = (_event: any, catalogItem?: ProductCatalogItem) => {
        setSelectedCatalogItem(catalogItem);
        setShowEditCatalogItemModal(!showEditCatalogItemModal);
    };

    const handleSubmit = (catalogItem: ProductCatalogItem) => {
        onUpdateCatalogItem(catalogItem);
        setSelectedCatalogItem(undefined);
        setShowEditCatalogItemModal(!showEditCatalogItemModal);
    };

    useEffect(() => {
        setDemographicItems(sortBy(catalogItems, "sequence"));
    }, [catalogItems]);

    const updateList = (items: any) => {
        setDemographicItems(items);

        const catalog = produce(value, (draft) => {
            draft.items = items;
        });

        onChange(catalog);
    };

    const handleDragEnd = (result: DropResult) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        const resultingSequence = getResultingSequence({
            destinationIndex: result.destination.index,
            sourceIndex: result.source.index,
            productCatalogItems: demographicItems,
            type: CatalogItemSequenceType.DEMOGRAPHICS,
        });

        const reOrderedItems = reorder(demographicItems, result.source.index, result.destination.index).map((item) => {
            if (item.name === result.draggableId) {
                return {
                    ...item,
                    sequence: resultingSequence,
                };
            }

            return item;
        });

        updateList(reOrderedItems);
    };

    const handleItemDelete = (index: number, item: ProductCatalogItem) => {
        demographicItems.splice(index, 1);
        updateList(demographicItems);
        onDeleteCatalogItem(item);
    };

    return (
        <>
            <DragDropContext onDragEnd={(result: DropResult) => handleDragEnd(result)}>
                <Droppable
                    droppableId="demographicTable"
                    renderClone={(provided, _, rubric) => (
                        <CloneTableRow
                            provided={provided}
                            item={demographicItems[rubric.source.index]}
                            index={rubric.source.index}
                            toggleEditCatalogItemModal={toggleEditCatalogItemModal}
                            handleItemDelete={handleItemDelete}
                        />
                    )}
                >
                    {(provided: DroppableProvided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            <Table>
                                <Table.Head>
                                    <Table.Row>
                                        <Table.Header className="!text-sm" />
                                        <Table.Header className="!text-sm">Demographic</Table.Header>
                                        <Table.Header className="!text-sm">Caption</Table.Header>
                                        <Table.Header className="text-right !text-sm">Actions</Table.Header>
                                    </Table.Row>
                                </Table.Head>

                                <Table.Body isStriped={false} className="max-h-51 overflow-y-scroll">
                                    {demographicItems.length > 0 ? (
                                        <>
                                            {demographicItems.map((item, index: number) => (
                                                <Draggable key={uniqueId("id-")} draggableId={item.name} index={index}>
                                                    {({ draggableProps, dragHandleProps, innerRef }, snapshot) => (
                                                        <tr
                                                            ref={innerRef}
                                                            {...draggableProps}
                                                            {...dragHandleProps}
                                                            key={item.template!.id}
                                                            className={clsx("justify-between", {
                                                                shadow: snapshot.isDragging,
                                                            })}
                                                            data-testid="demographic-catalog-editor-row"
                                                        >
                                                            <Table.Cell className="w-1/12">
                                                                <span className="inline-flex rounded border border-gray-light px-2 py-0.5">
                                                                    <MenuIcon size="small" />
                                                                </span>
                                                            </Table.Cell>
                                                            <Table.Cell className="w-4/12">
                                                                <NoTranslate
                                                                    if={!isDefaultDemographic(item.name)}
                                                                    className="whitespace-normal break-all"
                                                                >
                                                                    <DemographicIcon name={item.name} /> {item.name}
                                                                </NoTranslate>
                                                            </Table.Cell>
                                                            <Table.Cell className="w-5/12">
                                                                <NoTranslate className="whitespace-normal break-all">
                                                                    {item.caption}
                                                                </NoTranslate>
                                                            </Table.Cell>
                                                            <Table.Cell className="w-2/12 space-x-1 text-right">
                                                                <SecondaryButton
                                                                    className="mb-1"
                                                                    data-testid="demographic-catalog-editor-edit"
                                                                    size="tiny"
                                                                    onClick={(event: any) =>
                                                                        toggleEditCatalogItemModal(event, item)
                                                                    }
                                                                >
                                                                    <PenIcon />
                                                                </SecondaryButton>
                                                                <InlineConfirmDeletePopover
                                                                    onConfirm={() => handleItemDelete(index, item)}
                                                                >
                                                                    <SecondaryButton
                                                                        data-testid="demographic-catalog-editor-remove"
                                                                        className="mb-1"
                                                                        size="tiny"
                                                                    >
                                                                        <TrashIcon />
                                                                    </SecondaryButton>
                                                                </InlineConfirmDeletePopover>
                                                            </Table.Cell>
                                                        </tr>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </>
                                    ) : (
                                        <Table.Row>
                                            <Table.Cell colSpan={3} className="text-center">
                                                No Demographics
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                                </Table.Body>
                            </Table>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            <EditCatalogItemModal
                isOpen={showEditCatalogItemModal}
                catalogItem={selectedCatalogItem!}
                onClose={toggleEditCatalogItemModal}
                onSubmit={handleSubmit}
            />
        </>
    );
};

interface CloneTableRowProps {
    provided: any;
    item: any;
    index: number;
    toggleEditCatalogItemModal: (event: any, item: any) => void;
    handleItemDelete: (index: number, item: any) => void;
}

const CloneTableRow: React.FC<CloneTableRowProps> = ({
    provided,
    item,
    index,
    toggleEditCatalogItemModal,
    handleItemDelete,
}) => {
    return (
        <tr
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
                ...provided.draggableProps.style,
                background: "white",
                pointerEvents: "none",
            }}
        >
            <Table.Cell className="w-1/12">
                <span className="inline-flex rounded border border-gray-light px-2 py-0.5">
                    <MenuIcon size="small" />
                </span>
            </Table.Cell>
            <Table.Cell className="w-4/12">
                <NoTranslate if={!isDefaultDemographic(item.name)} className="whitespace-normal break-all">
                    <DemographicIcon name={item.name} /> {item.name}
                </NoTranslate>
            </Table.Cell>
            <Table.Cell className="w-5/12">
                <NoTranslate className="whitespace-normal break-all">{item.caption}</NoTranslate>
            </Table.Cell>
            <Table.Cell className="w-2/12 space-x-1 text-right">
                <SecondaryButton
                    className="mb-1"
                    data-testid="demographic-catalog-editor-edit"
                    size="tiny"
                    onClick={(event: any) => toggleEditCatalogItemModal(event, item)}
                >
                    <PenIcon />
                </SecondaryButton>
                <InlineConfirmDeletePopover onConfirm={() => handleItemDelete(index, item)}>
                    <SecondaryButton data-testid="demographic-catalog-editor-remove" className="mb-1" size="tiny">
                        <TrashIcon />
                    </SecondaryButton>
                </InlineConfirmDeletePopover>
            </Table.Cell>
        </tr>
    );
};
